import 'services/request-metrics'

import '@doist/marketist/dist/assets/style.css'

import 'components/product-ui/variables.css'

import '../styles/reset.css'
import '../styles/global.css'

import { useEffect } from 'react'

import { NextContextProvider, Theme, ThemeProvider, URLContext } from '@doist/marketist'

import { MSClarity } from 'components/msclarity'
import { nextProviderValues } from 'components/next-provider-values'
import { ProductUIThemeProvider } from 'components/product-ui/product-ui-theme-context'
import GlobalSeo from 'components/Seo/GlobalSeo'
import debounce from 'lodash/debounce'
import Head from 'next/head'
import { appWithTranslation } from 'next-i18next'
import { FeatureFlagContextProvider } from 'services/contexts/feature-flag-context'
import { flagsTracking } from 'services/feature-flags/flags-tracking'

import nextI18NextConfig from '../next-i18next.config.js'
import { caecilia, espa, graphik, inter, shantell } from '../styles/fonts'

import css from './app.module.css'

import type { AppProps } from 'next/app'
import type { NextPage } from 'next/types'
import type { CommonProps } from 'services/getCommonServerSideProps'

import { UserContextV2Provider } from '@/app/services/providers/contexts/user-context'

type LandingPagesAppProps = AppProps & {
    Component: NextPage
    pageProps: CommonProps['pageProps']
}

function LandingPagesApp({ Component, pageProps }: LandingPagesAppProps) {
    useEffect(() => {
        if (pageProps.featureFlags) {
            flagsTracking(pageProps.featureFlags.flags)
        }
    }, [pageProps.featureFlags])

    /**
     * Workaround for 100vw causing horizontal page scroll
     * Usage: inline-size: calc(var(--vw, 1vw) * 100);
     *
     * @see: https://www.smashingmagazine.com/2023/12/new-css-viewport-units-not-solve-classic-scrollbar-problem/
     */
    useEffect(() => {
        const observer = new ResizeObserver(
            debounce(() => {
                const vw = document.documentElement.clientWidth / 100
                document.documentElement.style.setProperty('--vw', `${vw}px`)
            }),
        )

        if (document.documentElement) {
            observer.observe(document.documentElement)
        }

        return () => {
            observer.disconnect()
        }
    }, [])

    const msClarityId = process.env.NEXT_PUBLIC_MS_CLARITY_ID

    return (
        <URLContext.Provider value={pageProps.absoluteUrl}>
            <ThemeProvider theme={Theme.todoist}>
                <UserContextV2Provider user={pageProps.user} workspaces={pageProps.workspaces}>
                    <FeatureFlagContextProvider session={pageProps.featureFlags?.flags}>
                        <NextContextProvider value={nextProviderValues}>
                            <Head>
                                {/* Browser toolbar color */}
                                <meta name="theme-color" content="#fff" />

                                <meta
                                    name="facebook-domain-verification"
                                    content={process.env.NEXT_PUBLIC_FACEBOOK_DOMAIN_VERIFICATION}
                                />
                                <meta
                                    name="viewport"
                                    content="width=device-width, initial-scale=1"
                                />
                            </Head>
                            <GlobalSeo />
                            {/* next/font doesn't work inside _document.tsx, so we have to inject styles here */}
                            <style
                                dangerouslySetInnerHTML={{
                                    __html: `
                                        :root {
                                            /* Override Marketist's font families, using the Next/font's 
                                            CSS Custom Property, defined in the font declaration file */
                                            --font-family-body: ${inter.style.fontFamily};
                                            --font-family-sans: ${graphik.style.fontFamily};
                                            --font-family-heading: ${graphik.style.fontFamily};
                                            --font-family-serif: ${caecilia.style.fontFamily};
                                            --font-family-handwritten: ${espa.style.fontFamily};
                                            --font-family-handwritten-base: ${shantell.style.fontFamily};
                                            }
                                        
                                            html {
                                                font-family: var(--font-family-body);
                                                }
                                            `,
                                }}
                            />
                            <ProductUIThemeProvider theme="red">
                                <div className={css.appContainer}>
                                    <Component {...pageProps} />
                                </div>
                            </ProductUIThemeProvider>
                        </NextContextProvider>
                    </FeatureFlagContextProvider>
                </UserContextV2Provider>
            </ThemeProvider>
            {msClarityId ? <MSClarity id={msClarityId} /> : null}
        </URLContext.Provider>
    )
}

export default appWithTranslation(LandingPagesApp, nextI18NextConfig)
